/**
 * AhirbhutamshaCalculator - Lazy calculator for D40 Ahirbhutamsha table data
 * Provides async calculation of 40 divisions (8 AHI × 5 BHUTA) with progress tracking
 */

import { JatakaResponse } from '../../../../../models/api';
import { lazyCalculator } from '../lazy/SimpleLazyCalculator';
import { calculateAhirbhutamshas } from '../data/AhirbhutamshaTableData';

/**
 * Ahirbhutamsha calculation result interface
 */
export interface AhirbhutamshaResult {
    divisions: Array<{
        planet: string;
        positionInRashi: number;
        ahiNumber: number;
        ahiType: string;
        bhutaNumber: number;
        bhutaType: string;
        amshaNumber: number;
        interpretation: string;
    }>;
    calculationTime: number;
    totalDivisions: number;
}

/**
 * Calculate Ahirbhutamsha (D40) divisions for all planets
 */
async function calculateAhirbhutamsha(
    apiResponse: JatakaResponse
): Promise<AhirbhutamshaResult> {
    const startTime = Date.now();

    // Simulate async calculation (actual calculation is sync but we wrap for consistency)
    await new Promise(resolve => setTimeout(resolve, 30)); // Minimal delay for UI feedback

    // Perform actual calculation
    const result = calculateAhirbhutamshas(apiResponse);

    // Transform to simple serializable format
    const divisions = result.divisions.map(div => ({
        planet: div.planet.short_name,
        positionInRashi: div.positionInRashi,
        ahiNumber: div.ahiNumber,
        ahiType: div.ahiType,
        bhutaNumber: div.bhutaNumber,
        bhutaType: div.bhutaType,
        amshaNumber: div.amshaNumber,
        interpretation: div.interpretation
    }));

    const calculationResult: AhirbhutamshaResult = {
        divisions,
        calculationTime: Date.now() - startTime,
        totalDivisions: divisions.length
    };

    return calculationResult;
}

/**
 * Generate cache key for Ahirbhutamsha calculation
 */
function generateCacheKey(apiResponse: JatakaResponse): string {
    if (!apiResponse.graha_positions?.planets) {
        return 'no-planets';
    }

    // Create cache key from planet longitudes (including Lagna, Rahu, Ketu)
    const planetPositions = apiResponse.graha_positions.planets
        .map(p => `${p.short_name}:${p.position}`)
        .join('|');

    return `d40-${planetPositions}`;
}

// Register with SimpleLazyCalculator
lazyCalculator.register({
    id: 'ahirbhutamsha-d40',
    calculator: calculateAhirbhutamsha,
    cacheKey: generateCacheKey,
    ttl: 1800000 // 30 minutes - manual invalidation when positions change
});

export { calculateAhirbhutamsha, generateCacheKey };
